'use client';

import { ServiceLayer } from '@packages/cms-components/src/modules/ServiceLayer/ServiceLayer';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { ProductHintsItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductHintsItemFragmentFragmentDoc';
import { useLayerTracking } from '@packages/modules/src/Tracking/hooks/useLayerTracking/useLayerTracking';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { dispatchDisplayOverlayTracking } from '@packages/shared/src/ui/Dialog/dispatchDialogOverlayTracking';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useServiceLayerTracking } from '../Tracking/hooks/useServiceLayerTracking';
import { productHintsItemMessages } from './messages';

/* GraphQL */ `
  fragment ProductHintsItemFragment on Hint {
    iconUrl
    text
    serviceLayerId
  }
`;

export type ProductHintsItemProps = {
  data: FragmentType<typeof ProductHintsItemFragmentFragmentDoc>;
};
/**
 * ProductHintsCard component displays all product hints
 * */
export const ProductHintsItem = ({ data }: ProductHintsItemProps) => {
  const dispatchGTMEvent = useTracking();
  const { iconUrl, serviceLayerId, text } = getFragmentData(
    ProductHintsItemFragmentFragmentDoc,
    data,
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { formatMessage } = useIntl();
  const { trackViewLayer } = useLayerTracking();
  const { trackServiceLayerClick } = useServiceLayerTracking();

  const handleClick = (sId: string) => {
    trackViewLayer(text);
    // handler can only be dispatched if a serviceLayerId is present
    trackServiceLayerClick(sId);
    dispatchDisplayOverlayTracking({
      eventDispatcher: dispatchGTMEvent,
      displayTrackingProps: { category: 'dv-service-layer-product-hints-item', label: text },
    });
    setIsDialogOpen(true);
  };

  return (
    <>
      <button
        type="button"
        className="flex w-full items-center gap-1 text-start"
        onClick={serviceLayerId ? () => handleClick(serviceLayerId) : undefined}
        disabled={!serviceLayerId}
      >
        <Image width={24} height={24} alt="" src={iconUrl} />
        <span className="text-size-body-sm">{text}</span>
      </button>
      {serviceLayerId !== null && (
        <Dialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
          <DialogHeader onClose={() => setIsDialogOpen(false)}>
            {formatMessage(productHintsItemMessages.cardHeader)}
          </DialogHeader>
          <DialogContent>
            <ServiceLayer serviceLayerId={serviceLayerId} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
